'use strict';

/**
 * shared/provenance/masthead-identity.js — the ONE fail-closed rule that decides whether a user
 * doc is allowed to become a public byline name.
 *
 * WHY THIS IS SHARED (and not just a frontend module)
 *   `shared/provenance/provenance.js` is pure and takes identity as an INJECTED map, so EVERY
 *   surface that derives provenance must resolve `uid → identity` itself. There are two runtimes
 *   that do: the Next.js frontend (src/lib/masthead-identity.ts, admin SDK) and the backend MCP
 *   data-fetchers (backend/src/mcp/lib/masthead-identity.mjs, admin SDK). The I/O + caching layers
 *   legitimately differ per runtime; the RULE must not. A second hand-written copy of "require an
 *   explicit mastheadName, never fall back to displayName/email/twitterUsername, reject a
 *   non-http(s) url" is exactly the kind of subtle duplication that drifts silently and ends with
 *   one surface publishing a name nobody put on the masthead. So the rule lives HERE, once, and
 *   both runtimes import it — the same cross-runtime idiom as provenance.js itself (CJS, zero
 *   requires, imported by TS via `@shared/…` and by the backend `.mjs` by relative path).
 *
 * PURITY CONTRACT
 *   Requires NOTHING (no firebase, no env, no clock) beyond the global URL parser. The Firestore
 *   read, the cache, and the error degradation belong to the callers.
 *
 * FAIL-CLOSED
 *   `pickMastheadIdentity` requires an explicit, non-empty `mastheadName`. It NEVER infers a public
 *   name from `displayName`, `email`, or `twitterUsername` — those are account fields a user sets
 *   for unrelated reasons. No mastheadName → no identity → the byline UNDER-claims (grammar B).
 */

/** The user-doc field that opts a user into the masthead. Callers query on it (`!= ''`). */
const MASTHEAD_NAME_FIELD = 'mastheadName';

/** A trimmed non-empty string, else null. */
function nonEmpty(v) {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s.length ? s : null;
}

/**
 * An http(s) URL, else null — the value lands in an href, so no javascript:/data:/vbscript:/
 * protocol-relative bypass ever reaches it. Returns the PARSED `u.href`, not the raw input: this
 * function is the validation boundary, so what it emits must be what it actually validated (a raw
 * pass-through could carry characters `new URL()` would have percent-encoded).
 */
function safeMastheadUrl(v) {
  const s = nonEmpty(v);
  if (!s) return null;
  try {
    const u = new URL(s);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null;
  } catch (_) {
    return null;
  }
}

/**
 * PURE. The masthead identity carried by a user doc, or null when the doc has not opted into the
 * masthead. Zero I/O so the fail-closed rules are unit-testable without firebase, in either runtime.
 *
 * @param {unknown} userDoc a raw `users` document's data
 * @returns {{name:string, handle:string|null, url:string|null}|null}
 */
function pickMastheadIdentity(userDoc) {
  if (!userDoc || typeof userDoc !== 'object') return null;
  const name = nonEmpty(userDoc[MASTHEAD_NAME_FIELD]);
  if (!name) return null; // the ONLY gate — never infer a public name from another field
  return { name, handle: nonEmpty(userDoc.mastheadHandle), url: safeMastheadUrl(userDoc.mastheadUrl) };
}

/**
 * PURE. Fold a Firestore QuerySnapshot's `docs` into the injectable identity map (uid → identity),
 * skipping every doc the fail-closed rule rejects. Shared so neither runtime can accidentally keep
 * a rejected doc (e.g. by writing `map[id] = pickMastheadIdentity(d) || {}`).
 *
 * @param {Array<{id:string, data:function():unknown}>|null|undefined} docs
 * @returns {Object<string,{name:string,handle:string|null,url:string|null}>}
 */
function identityMapFromDocs(docs) {
  const map = {};
  if (!Array.isArray(docs)) return map;
  for (const doc of docs) {
    if (!doc || typeof doc.data !== 'function') continue;
    const identity = pickMastheadIdentity(doc.data());
    if (identity) map[doc.id] = identity;
  }
  return map;
}

module.exports = {
  MASTHEAD_NAME_FIELD,
  // Exported because provenance.js re-validates through it at the point the url is EMITTED (see
  // identityForUid). One http(s) rule, applied at both the map-build boundary and the emit boundary.
  safeMastheadUrl,
  pickMastheadIdentity,
  identityMapFromDocs,
};
